import { MDSListBox } from "@/mds";
import styled from "@emotion/styled";
import { observer } from "mobx-react-lite";
import { FC, useCallback, useMemo } from "react";
import { MdsIcon, MdsIconKind } from "@/design-system/components/icon";
import { useAppStore } from "@/store";
import { MdsButton, MdsButtonSize, MdsButtonVariant } from "@/design-system/components/button";
import { toastModule } from "@/modules/toast";
import { windowModule } from "@/modules/window";
import { APP_CONFIG } from "@/domains/global/config";
import {
  StyledContentListText,
  StyledContentWrapper,
} from "@/pages/integrations/shared/integrations.styles";
import { IntegrationsZapierPageDescription } from "@/pages/integrations/zapier/integrations-zapier-page-description";
import { useRefocus } from "@/domains/react/useRefocus";
import { IntegrationsLayoutBottomBuffer } from "@/pages/integrations/shared/integrations-layout";
import { TrackedEvent } from "@/domains/metrics/tracked-event";
import { trackEvent } from "@/domains/metrics/segment";

export const IntegrationsZapierPageContent: FC = observer(() => {
  const { store } = useAppStore();
  const { isEnabled, disable, isDisabling } = store.zapierIntegration;
  const isConnected = isEnabled;

  useRefocus(
    () => {
      /**
       * Whenever we refocus this page, we reload the zapier integration settings.
       * (The zapier setup happens in a different tab, so we want to make sure we
       * reflect the latest status when they return to this page [without requiring
       * them to manually refresh].)
       */
      store.zapierIntegration.reload();
    },
    { immediate: true }
  );

  const handleDisconnectZapier = useCallback(async () => {
    try {
      await disable();
      trackEvent(TrackedEvent.ZapierDisconnect);

      toastModule.triggerToast({
        content: "Disconnected",
      });
    } catch (_unknownError) {
      toastModule.triggerToast({
        content: "Something went wrong. Please try again later.",
      });
    }
  }, [disable]);

  const handleClickZapierIntegrationLink = useCallback(() => {
    trackEvent(TrackedEvent.ZapierIntegrationLinkClick);
    windowModule.openInNewTab({ url: new URL(APP_CONFIG.URLS.ZAPIER_INTEGRATION) });
  }, []);

  const listItems = useMemo(() => {
    if (isEnabled) {
      return [
        {
          id: "1",
          children: (
            <StyledContentListText>
              <RowBoldText>Connected</RowBoldText>
              <MdsIcon kind={MdsIconKind.CheckCircleSolid} />
            </StyledContentListText>
          ),
          rightAddon: (
            <MdsButton
              label="Disconnect"
              variant={MdsButtonVariant.Outlined}
              size={MdsButtonSize.Small}
              isLoading={isDisabling}
              onClick={handleDisconnectZapier}
            />
          ),
        },
      ];
    }

    return [
      {
        id: "1",
        children: (
          <StyledContentListText>
            <RowBoldText>Not Connected</RowBoldText>
          </StyledContentListText>
        ),
        rightAddon: (
          <MdsButton
            label="Set up in Zapier"
            variant={MdsButtonVariant.Outlined}
            size={MdsButtonSize.Small}
            onClick={handleClickZapierIntegrationLink}
          />
        ),
      },
    ];
  }, [isEnabled, isDisabling, handleDisconnectZapier, handleClickZapierIntegrationLink]);

  return (
    <StyledContentWrapper>
      <IntegrationsZapierPageDescription
        isConnected={isConnected}
        onZapierIntegrationLinkClick={handleClickZapierIntegrationLink}
      />
      <MDSListBox items={listItems} />
      <IntegrationsLayoutBottomBuffer />
    </StyledContentWrapper>
  );
});

const RowBoldText = styled.div`
  font-weight: ${({ theme }) => theme.fontWeights.medium};
`;
